'use client';

import { ReactNode, SyntheticEvent, useState } from 'react';
import Close from '@mui/icons-material/Close';
import Autocomplete, { AutocompleteChangeDetails, AutocompleteChangeReason } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import { animationEaseOut } from '@/styles/animation';
import { SxProps, Theme } from '@mui/material/styles';

interface SearchableSelectProps<T> {
	labelText: string;
	options?: T[];
	value?: T | T[] | null;
	multiple?: boolean;
	onChange?: (
		event: SyntheticEvent,
		value: string | T | (string | T)[] | null,
		reason: AutocompleteChangeReason,
		details?: AutocompleteChangeDetails<T> | undefined,
	) => void;
	getOptionLabel?: (option: T | string) => string;
	placeholder?: string;
	disabled?: boolean;
	required?: boolean;
	error?: boolean;
	helperText?: ReactNode;
	startAdornment?: ReactNode;
	clearIcon?: ReactNode;
	sx?: SxProps<Theme>;
}

export default function SearchableSelect<T>({
	labelText,
	options = [],
	value,
	multiple = false,
	onChange,
	getOptionLabel,
	placeholder,
	disabled = false,
	required = false,
	error = false,
	helperText,
	startAdornment,
	clearIcon,
	sx,
}: Readonly<SearchableSelectProps<T>>) {
	const optionLabel = (option: T | string) => getOptionLabel?.(option) ?? String(option);
	const [isFocused, setIsFocused] = useState(false);
	const hasChips = multiple && Array.isArray(value) && value.length > 0;

	return (
		<Autocomplete
			freeSolo
			multiple={multiple}
			value={value ?? (multiple ? [] : '')}
			options={options}
			onChange={onChange}
			getOptionLabel={optionLabel}
			isOptionEqualToValue={(option, optionValue) => optionLabel(option) === optionLabel(optionValue)}
			disabled={disabled}
			data-testid="searchable-select"
			clearIcon={clearIcon}
			sx={sx}
			renderInput={(params) => {
				const { InputProps, inputProps, ...rest } = params;

				const isEmpty = !inputProps?.value;
				const isShrunk = !isEmpty || isFocused || hasChips;
				const showCloseIcon = (!isShrunk && !!value && !isEmpty) || isShrunk;

				return (
					<TextField
						{...rest}
						label={labelText}
						placeholder={placeholder}
						variant="outlined"
						required={required}
						error={error}
						helperText={helperText}
						InputLabelProps={{
							shrink: isShrunk,
							sx: {
								...(startAdornment ? { left: isShrunk ? 0 : 32 } : {}),
								transition: `color 200ms ${animationEaseOut} 0ms,transform 200ms ${animationEaseOut} 0ms, max-width 200ms ${animationEaseOut} 0ms, left 150ms ${animationEaseOut}`,
							},
						}}
						InputProps={{
							...InputProps,
							startAdornment: startAdornment && (
								<>
									{startAdornment}
									{InputProps.startAdornment}
								</>
							),
							sx: {
								gap: 1,
								'& .MuiAutocomplete-clearIndicator': {
									visibility: showCloseIcon ? 'visible' : 'hidden',
								},
								'&:hover .MuiAutocomplete-clearIndicator': {
									visibility: showCloseIcon ? 'visible' : 'hidden',
								},
							},
						}}
						inputProps={{
							...inputProps,
							onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
								setIsFocused(true);
								inputProps?.onFocus?.(e);
							},
							onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
								setIsFocused(false);
								inputProps?.onBlur?.(e);
							},
							style: {
								paddingLeft: 0,
								paddingRight: 0,
							},
						}}
						sx={{
							'& .MuiOutlinedInput-root.MuiOutlinedInput-root': {
								paddingLeft: '14px',
								paddingRight: '14px',
							},
						}}
					/>
				);
			}}
			renderOption={(props, option) => {
				const { key, ...optionProps } = props;
				return (
					<Box component="li" key={key} {...optionProps}>
						{optionLabel(option)}
					</Box>
				);
			}}
			renderTags={
				multiple
					? (tags: T[], getTagProps) =>
							tags.map((option, index) => {
								const { key, ...tagProps } = getTagProps({ index });
								return (
									<Chip
										data-testid="searchable-select-chip"
										label={optionLabel(option)}
										key={key}
										deleteIcon={<Close />}
										{...tagProps}
									/>
								);
							})
					: undefined
			}
		/>
	);
}
